import { gql } from '@apollo/client';
import { Formik } from 'formik';
import _, { identity, range } from 'lodash';
import { useMemo } from 'react';
import { FormGroup, Input, Label } from 'reactstrap';
import * as yup from 'yup';
import { CARBON_NAME } from '../../constants/revenueStreams';
import { DISTANCE_UNITS, SMALL_DISTANCE_UNITS } from '../../constants/units';
import { N_YEARS } from '../../constants/years';
import {
  UpsertCropTemplateMutation,
  UpsertFarmCropMutation,
} from '../../mutations';
import {
  useDataScope,
  useFarmName,
  useRecord,
  useStartYear,
} from '../../utils/hooks';
import { objectWithFields, thirtyYearArray } from '../../utils/utils';
import { nullableNumber } from '../../utils/yup';
import { CropTemplatesQuery, RawCropsQuery } from './CropsTable';
import {
  Message,
  SubmitButton,
  TemplateForm,
  ValidatedInput,
  YearInputs,
} from './template-components';
import TableCsvButton from '../tables/TableCsvButton';
import {
  asPercentage,
  numberAsCurrencyWithCents,
  numberAsDynamicRoundedDecimal,
} from '../../utils/formatters';
import { useUnits } from '../../contexts/UnitContext';

const fields = [
  'id',
  'belowGroundMassPercentOfAbove',
  'branchMassPercentOfTrunk',
  'dbhInInches',
  'soilCarbonPercentOfAbove',
  'trunkHeightInFeet',
  'woodDensity',
];

const queryString = `
    id
    updatedAt
    name
    carbon {
      ${fields.join(' ')}
    }
    revenueStreams {
      id
      name
      unitCost
    }
  `;

const CropTemplateQuery = gql`
  query CropTemplateCarbon($id: ID!) {
    cropTemplate(id: $id) {
      ${queryString}
      team {
        id
      }
    }
  }
`;

const RawCropQuery = gql`
  query RawCropCarbon($id: ID!) {
    rawCrop(id: $id) {
      ${queryString}
    }
  }
`;

const defaultCarbon = {
  ...objectWithFields(fields),
  belowGroundMassPercentOfAbove: thirtyYearArray(''),
  branchMassPercentOfTrunk: thirtyYearArray(''),
  dbhInInches: thirtyYearArray(''),
  soilCarbonPercentOfAbove: thirtyYearArray(''),
  trunkHeightInFeet: thirtyYearArray(''),
};

const carbonPropertySchema = yup
  .array()
  .length(N_YEARS)
  .of(nullableNumber().min(0, 'Minimum is 0'))
  // technically the BE doesn't require year 30 to be set to work
  // keep as is for now because I think the interpolation rules
  // are a little clearer when defined this way
  // but can change later as needed
  .test({
    name: `year-${N_YEARS}-set`,
    message: `Year ${N_YEARS} is required`,
    test: (value) => _.isNumber(value[N_YEARS - 1]),
  })
  .required();

const carbonSchema = yup.object().shape({
  belowGroundMassPercentOfAbove: carbonPropertySchema,
  branchMassPercentOfTrunk: carbonPropertySchema,
  dbhInInches: carbonPropertySchema,
  soilCarbonPercentOfAbove: carbonPropertySchema,
  trunkHeightInFeet: carbonPropertySchema,
  woodDensity: yup.number().min(0, 'Minimum is 0').required('Required'),
});

const carbonRevenueSchema = yup.object().shape({
  name: yup
    .string()
    .required('Required')
    .test({
      name: 'carbon-revenue-name',
      message: 'Name is required',
      test: (value) => value === CARBON_NAME,
    }),
  unitCost: yup
    .array()
    .of(yup.number().min(0, 'Minimum is 0').required('Required'))
    .length(1, 'Required'),
});

const schema = yup.object().shape({
  carbon: carbonSchema,
  carbonRevenue: carbonRevenueSchema.nullable().default(null),
});

const transformValues = (
  { carbon, carbonRevenue },
  { id, updatedAt, team },
) => ({
  id,
  updatedAt,
  teamId: team?.id,
  carbon: {
    ...carbon,
    id: undefined,
    __typename: undefined,
  },
  ...(carbonRevenue
    ? { revenueStreams: [{ ...carbonRevenue, __typename: undefined }] }
    : {}),
});

const getColumns = (startYear) => [
  { displayString: 'Year', accessor: 'metric' },
  ...range(N_YEARS).map((yearIndex) => ({
    displayString: `${startYear + yearIndex}`,
    accessor: (item) => item.data?.[yearIndex] ?? null,
    format: numberAsDynamicRoundedDecimal,
  })),
];

const transformData = (
  carbon,
  carbonRevenue,
  distanceUnits,
  smallDistanceUnits,
) => {
  const {
    transformToUnit: distanceTransformToUnit = identity,
    displayUnit: distanceDisplayUnit,
  } = distanceUnits;
  const {
    transformToUnit: smallDistanceTransformToUnit = identity,
    displayUnit: smallDistanceDisplayUnit,
  } = smallDistanceUnits;
  return [
    {
      metric: `DBH (${smallDistanceDisplayUnit})`,
      data: carbon.dbhInInches.map(smallDistanceTransformToUnit),
    },
    {
      metric: `Trunk height (${distanceDisplayUnit})`,
      data: carbon.trunkHeightInFeet.map(distanceTransformToUnit),
    },
    {
      metric: 'Branch mass % of trunk',
      data: carbon.branchMassPercentOfTrunk,
      format: asPercentage,
    },
    {
      metric: 'Below ground mass % of above',
      data: carbon.belowGroundMassPercentOfAbove,
      format: asPercentage,
    },
    {
      metric: 'Soil carbon % of above',
      data: carbon.soilCarbonPercentOfAbove,
      format: asPercentage,
    },
    { metric: 'Wood density (kg/m³)', data: [carbon.woodDensity] },
    ...(carbonRevenue && !carbonRevenue.isDeleted
      ? [
          {
            metric: 'Price per ton',
            data: carbonRevenue.unitCost,
            format: numberAsCurrencyWithCents,
          },
        ]
      : []),
  ];
};

const EditCarbon = ({ onSubmit, status, resource, msg }) => {
  const scope = useDataScope();
  const startYear = useStartYear();
  const farmName = useFarmName(scope !== 'farm');
  const distanceUnits = useUnits(DISTANCE_UNITS);
  const smallDistanceUnits = useUnits(SMALL_DISTANCE_UNITS);
  const { loading } = status;

  const initialValues = useMemo(() => {
    const carbonRevenue = resource?.revenueStreams?.find(
      ({ name }) => name === CARBON_NAME,
    );
    return {
      carbon: resource?.carbon || defaultCarbon,
      carbonRevenue,
    };
  }, [resource]);

  if (msg) return <Message>{msg}</Message>;

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit(schema, 'Carbon settings updated')}
    >
      {({ values, setFieldValue }) => {
        const hasCarbonStream =
          !!values.carbonRevenue && !values.carbonRevenue.isDeleted;
        return (
          <TemplateForm>
            <div className="d-flex">
              <ValidatedInput
                type="number"
                name="carbon.woodDensity"
                label="Wood density"
                min={0}
                step="any"
                unit="kg/m³"
              />
              <div className="d-flex">
                <FormGroup>
                  <Label>Carbon Revenue</Label>
                  <Input
                    type="checkbox"
                    onChange={() =>
                      setFieldValue(
                        'carbonRevenue',
                        values.carbonRevenue?.id
                          ? // If the server already has a carbon revenue,
                            // set or unset the isDeleted flag
                            {
                              ...values.carbonRevenue,
                              isDeleted:
                                // The server doesn't like isDeleted=false
                                !values.carbonRevenue.isDeleted || undefined,
                            }
                          : // Otherwise, create or remove the carbon revenue stream
                          values.carbonRevenue
                          ? null
                          : { unitCost: [0], name: CARBON_NAME, unit: 'ton' },
                      )
                    }
                    checked={hasCarbonStream}
                  />
                </FormGroup>
                {hasCarbonStream && (
                  <ValidatedInput
                    type="number"
                    name="carbonRevenue.unitCost[0]"
                    label="Price"
                    unit="/ton"
                    currency
                    min={0}
                    step={0.01}
                  />
                )}
              </div>
            </div>
            <YearInputs
              accessor="carbon.dbhInInches"
              min={0}
              step="any"
              unitSpec={SMALL_DISTANCE_UNITS}
              label="DBH"
            />
            <YearInputs
              accessor="carbon.trunkHeightInFeet"
              min={0}
              step="any"
              unitSpec={DISTANCE_UNITS}
              label="Trunk Height"
            />
            <YearInputs
              accessor="carbon.branchMassPercentOfTrunk"
              min={0}
              step="any"
              label="Branch Mass % of Trunk"
            />
            <YearInputs
              accessor="carbon.belowGroundMassPercentOfAbove"
              min={0}
              step="any"
              label="Root Mass % of Above"
            />
            <YearInputs
              accessor="carbon.soilCarbonPercentOfAbove"
              min={0}
              step="any"
              label="Soil Carbon % of Above"
            />
            <SubmitButton loading={loading} />
            {resource.carbon != null && (
              <TableCsvButton
                className="ml-2"
                cols={getColumns(startYear)}
                data={transformData(
                  values.carbon,
                  values.carbonRevenue,
                  distanceUnits,
                  smallDistanceUnits,
                )}
                title={[
                  `${resource.name} Carbon ${
                    scope === 'farm' ? '' : 'Template '
                  }Settings`,
                  farmName,
                ]
                  .filter((v) => v)
                  .join(' - ')}
              />
            )}
          </TemplateForm>
        );
      }}
    </Formik>
  );
};

export const EditTemplateCropCarbon = () => {
  const props = useRecord({
    submitMutation: UpsertCropTemplateMutation,
    fetchRecordQuery: CropTemplateQuery,
    onDeleteRefetchQuery: CropTemplatesQuery,
    transformValues,
  });
  return <EditCarbon {...props} />;
};

export const EditFarmCropCarbon = () => {
  const props = useRecord({
    submitMutation: UpsertFarmCropMutation,
    fetchRecordQuery: RawCropQuery,
    onDeleteRefetchQuery: RawCropsQuery,
    transformValues,
    includeFarmId: true,
  });
  return <EditCarbon {...props} />;
};
